import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Typography,
  Divider,
  FormHelperText,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import {
  DependencyDetails,
  SeverityEnum,
  ImpactAreaEnum,
  TypeEnum,
  DependencyStatusEnum,
  PriorityEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers
} from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { getDefaultExpectedDate } from './raidFormUtils';

interface DependencyDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: DependencyDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean; // Add read-only mode for when review is submitted
  isFromOtherQuestionnaire?: boolean; // Indicates if item is from another questionnaire
  defaultAssigneeId?: string | number | null;
  initialPartialRaid?: boolean;
}

const DependencyDialog: React.FC<DependencyDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false, // Default to false for backward compatibility
  isFromOtherQuestionnaire = false,
  defaultAssigneeId,
  initialPartialRaid = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<DependencyDetails>({
    description: '',
    severity: SeverityEnum.MEDIUM,
    impact_area: ImpactAreaEnum.EFFORT,
    type: TypeEnum.INTERNAL,
    person_responsible: '',
    priority: PriorityEnum.MEDIUM,
    status: DependencyStatusEnum.OPEN,
  });
  const [formTitle, setFormTitle] = useState('');
  const [expectedFulfillmentDate, setExpectedFulfillmentDate] = useState('');
  const [actualFulfillmentDate, setActualFulfillmentDate] = useState('');
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when initialData changes
  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        // Filter out System User and inactive users
        const filteredUsers = response.data.filter((user: User) => 
          user.display_name !== 'System User' && 
          user.user_name !== 'system_user' &&
          user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Helper function to find user by ID
  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find(u => u.id.toString() === userId) || null;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        status: initialData.status || DependencyStatusEnum.OPEN,
      });
      
      // Set dates from initialData if they exist
      if (initialData.expected_fulfuillment_date) {
        setExpectedFulfillmentDate(initialData.expected_fulfuillment_date);
      }
      if (initialData.actual_fulfillment_date) {
        setActualFulfillmentDate(initialData.actual_fulfillment_date);
      }
    } else {
      // Reset form for new entries
      setFormData({
        description: '',
        severity: SeverityEnum.MEDIUM,
        impact_area: ImpactAreaEnum.EFFORT,
        type: TypeEnum.INTERNAL,
        person_responsible: '',
        priority: PriorityEnum.MEDIUM,
        status: DependencyStatusEnum.OPEN,
      });
      
      // Set current date as default for new items
      const currentDate = getCurrentDate();
      setExpectedFulfillmentDate(currentDate);
      setActualFulfillmentDate(currentDate);
    }
    
    // Set the title
    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
  }, [initialData, title, initialPartialRaid, itemId]);

  const applyPartialRaidDefaults = () => {
    if (initialData) return;
    if (!formData.person_responsible && defaultAssigneeId != null && defaultAssigneeId !== '') {
      setFormData(prev => ({
        ...prev,
        person_responsible: String(defaultAssigneeId),
      }));
    }
    if (!expectedFulfillmentDate) {
      setExpectedFulfillmentDate(getDefaultExpectedDate());
    }
  };

  const handleTextChange = (field: keyof DependencyDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSelectChange = (field: keyof DependencyDetails) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    
    // Clear error when title is updated
    if (errors.title) {
      setErrors(prev => ({
        ...prev,
        title: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.person_responsible?.trim()) {
      newErrors.person_responsible = 'Responsible person is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Prepare the data with dates
      const submitData: RaidItemCreate = {
        entity_id: 0, // Will be set by parent component
        title: formTitle,
        raid_category: RaidCategoryEnum.DEPENDENCY,
        partial_raid: effectivePartialRaid,
        details: {
          ...formData,
          expected_fulfuillment_date: expectedFulfillmentDate || undefined,
          actual_fulfillment_date: actualFulfillmentDate || undefined,
        }
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting dependency item:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? 'View Dependency' : (itemId ? 'Update Dependency' : 'Add New Dependency')}
        {loading && (
          <CircularProgress
            size={20}
            sx={{
              marginLeft: 2,
              verticalAlign: 'middle'
            }}
          />
        )}
      </DialogTitle>
      
      <DialogContent>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire 
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'
            }
          </Typography>
        )}
        {!readOnly && isCreating && (
          <FormControlLabel
            control={
              <Switch
                checked={partialRaid}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setPartialRaid(enabled);
                  if (enabled) {
                    applyPartialRaidDefaults();
                  }
                }}
              />
            }
            label="Partial RAID (show only required fields)"
            sx={{ mb: 1 }}
          />
        )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                value={formTitle}
                onChange={handleTitleChange}
                fullWidth
                required
                error={!!errors.title}
                helperText={errors.title || 'A concise title for the dependency'}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={handleTextChange('description')}
                multiline
                rows={3}
                fullWidth
                required
                error={!!errors.description}
                helperText={errors.description || 'Detailed description of the dependency'}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>

            {!effectivePartialRaid && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={handleSelectChange('type') as any}
                      readOnly={readOnly}
                      inputProps={{ readOnly }}
                    >
                      {Object.values(TypeEnum).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Impact Area</InputLabel>
                    <Select
                      value={formData.impact_area}
                      label="Impact Area"
                      onChange={handleSelectChange('impact_area') as any}
                      readOnly={readOnly}
                      inputProps={{ readOnly }}
                    >
                      {Object.values(ImpactAreaEnum).map((area) => (
                        <MenuItem key={area} value={area}>
                          {area}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={handleSelectChange('priority') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(PriorityEnum).map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  label="Severity"
                  onChange={handleSelectChange('severity') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(SeverityEnum).map((severity) => (
                    <MenuItem key={severity} value={severity}>
                      {severity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {!effectivePartialRaid && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleSelectChange('status') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(DependencyStatusEnum).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.display_name}
                value={findUserById(formData.person_responsible || '')}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    person_responsible: newValue ? newValue.id.toString() : '' 
                  }));
                }}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Person Responsible"
                    required
                    error={!!errors.person_responsible}
                    helperText={errors.person_responsible || 'Person responsible for fulfilling this dependency'}
                    InputProps={{ ...params.InputProps, readOnly }}
                    disabled={readOnly}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No users found"
              />
            </Grid>

            {!effectivePartialRaid && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Fulfillment Dates
                </Typography>
                <Divider />
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected Fulfillment Date"
                type="date"
                value={expectedFulfillmentDate}
                onChange={(e) => setExpectedFulfillmentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly }}
                disabled={readOnly}
                fullWidth
              />
            </Grid>

            {!effectivePartialRaid && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Fulfillment Date"
                  type="date"
                  value={actualFulfillmentDate}
                  onChange={(e) => setActualFulfillmentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DependencyDialog;
